import React, { useEffect, useState } from 'react';
import clsx from "clsx";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Divider, Form, Spin, Input, Space, Steps, Upload, message as antdMessage } from "antd";
import styled from "styled-components";
import Config from "../../config";
import axios from '../../utils/axios';
import { getContent, toggleSpinner, editContent, setContent, getOpportunity, getProgramById, getIndicatorAnswers } from "../../redux/actions";
import StyledText from "../../components/StyledText";
import Button from "../../components/Button";
import { getLocalStorageItem, uploadFile, getSecuredUrl } from "../../utils/utilities";
import {
    toggleSpinnerSelector,
    opportunityDataSelector,
    savedContentListSelector,
    savedContentDataSelector,
    indicatorDataSelector
} from "../../redux/selectors";
import {
    useAppDispatch,
    useAppSelector
} from "../../redux/store";
import { EyeOutlined, DeleteOutlined, MinusCircleOutlined } from '@ant-design/icons';

const { TextArea } = Input;

const formItemLayout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 12 },
};

const CharityFinalCaseStudies = ({ className }) => {
    const baseClassName = clsx("charityFinalCaseStudies", className);
    const intl = useIntl();
    const [pictureMediaDescriptions, setPictureMediaDescriptions] = useState([]);
    const [mediaDescriptions, setMediaDescriptions] = useState([]);
    const [mediaPictures, setMediaPictures] = useState([]);
    const [docs, setDocs] = useState([]);
    const [docsMediaDescriptions, setDocsMediaDescriptions] = useState([]);
    const [formData, setFormData] = useState({});
    const [collapsedItems, setCollapsedItems] = useState({});
    const [pictures, setPictures] = useState([]);

    const [savedClicked, setSavedClicked] = useState(false);
    const opportunityData = useAppSelector(opportunityDataSelector) || {}
    const indicators = useAppSelector(indicatorDataSelector) || {};
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const showSpinner = useAppSelector(toggleSpinnerSelector);
    const { ID } = useParams();
    const { ID: contentId,
        caseStudy,
        gallery,
        uploadedDocuments
    } = useAppSelector(savedContentDataSelector) || {}
    const { opportunityID } = JSON.parse(getLocalStorageItem('contentData') || '{}');
    const { opportunity:
        {
            programID,
        } = {} }
        = opportunityData || {};
    const { message } = useAppSelector(savedContentListSelector) || {};

    const handleSave = async (index, savedName) => {
        try {
            const caseStudyFields = await form.validateFields([['caseStudy', index, 'name'], ['caseStudy', index, 'outcome'], ['caseStudy', index, 'quoteBy']]);
            if (caseStudyFields) {
                setCollapsedItems((prev) => {
                    if (!prev) return {};
                    const updatedItems = { ...prev };
                    updatedItems[index] = {
                        ...updatedItems[index],
                        [savedName]: true
                    };
                    return updatedItems;
                });
            }
        } catch (error) {
            console.error("Validation failed", error);
        }
    };

    const handleEdit = (index, savedName) => {
        setCollapsedItems((prev) => {
            if (!prev) return {};
            const updatedItems = { ...prev };
            Object.keys(updatedItems).forEach((key) => {
                if (
                    updatedItems?.[index] &&
                    typeof updatedItems[index] === 'object' &&
                    updatedItems[index]?.[savedName] !== undefined
                ) {
                    updatedItems[index] = {
                        ...updatedItems[index],
                        [savedName]: false
                    };
                }
            });
            return updatedItems;
        });
    };

    useEffect(() => {
        if (contentId) {
            const data = {
                ...(caseStudy && { caseStudy: caseStudy }),
            };
            if (gallery) {
                const fetchMediaUrls = async () => {
                    const updatedMedia = await Promise.all(
                        gallery.map(async (pic) => {
                            if (pic.key && !pic.url) {
                                try {
                                    const blob = await getSecuredUrl(pic.key, true);
                                    const fullImageUrl = URL.createObjectURL(blob);
                                    return { ...pic, url: fullImageUrl };
                                } catch (error) {
                                    console.error(`Failed to fetch URL for ${pic.key}:`, error);
                                    return pic;
                                }
                            }
                            return pic;
                        })
                    );
                    setMediaPictures(updatedMedia);
                    const initialDescriptions = updatedMedia.reduce((acc, pic) => {
                        acc[pic.uid] = pic.mediaDescription || '';
                        return acc;
                    }, {});
                    setMediaDescriptions(initialDescriptions);
                };
                fetchMediaUrls();
            }
            if (uploadedDocuments) {
                setDocs(uploadedDocuments);
                const initialDescriptions = uploadedDocuments.reduce((acc, pic) => {
                    acc[pic.uid] = pic.mediaDescription || '';
                    return acc;
                }, {});
                setDocsMediaDescriptions(initialDescriptions);
            }
            const collapsed = caseStudy?.reduce((acc, el, index) => {
                acc[index] = { ...el, [el.quoteBy]: true };
                return acc;
            }, {});
            const savedPictures = caseStudy?.map((el) => [...(el?.documents || [])]);

            if (Array.isArray(savedPictures) && savedPictures.length > 0) {
                const fetchMediaUrls = async () => {
                    const updatedMedia = await Promise.all(
                        savedPictures.map(async (pic) => {
                            if (pic.key && !pic.url) {
                                try {
                                    const blob = await getSecuredUrl(pic.key, true);
                                    const fullImageUrl = URL.createObjectURL(blob);
                                    return { ...pic, url: fullImageUrl };
                                } catch (error) {
                                    console.error(`Failed to fetch URL for ${pic.key}:`, error);
                                    return pic;
                                }
                            }
                            return pic;
                        })
                    );
                    setPictures(updatedMedia);
                    const initialDescriptions = updatedMedia.reduce((acc, pic) => {
                        acc[pic.uid] = pic.mediaDescription || '';
                        return acc;
                    }, {});
                    setPictureMediaDescriptions(initialDescriptions);
                };
                fetchMediaUrls();
            }

            setCollapsedItems(collapsed)
            form.setFieldsValue(data);
            setFormData(data);
            dispatch(setContent(''))
        }
    }, [contentId]);

    useEffect(() => {
        if (message && !savedClicked) {
            dispatch(setContent(''))
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content/final/next-steps/${ID}`);
        } else if (message && savedClicked) {
            dispatch(setContent(''))
            dispatch(toggleSpinner(false));
            navigate(`/impact-maker/content`);
        }
    }, [message])

    useEffect(() => {
        if (opportunityID) {
            document?.documentElement?.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
            dispatch(getContent({ contentID: ID }));
            dispatch(getOpportunity(opportunityID));
        }
    }, [opportunityID]);


    useEffect(() => {
        if (programID) {
            if (opportunityID) {
                dispatch(getIndicatorAnswers({
                    programID,
                    opportunityID: opportunityID
                }))
            }
            dispatch(getProgramById(programID));
            dispatch(toggleSpinner(false));
        }
    }, [programID]);

    const onFinishFailed = (e) => {
        console.log("Failed:", e);
    };

    const handleRemove = (savedName, remove) => {
        remove(savedName);
        const collapsed = caseStudy?.filter((el, index) => el.quoteBy !== savedName);
        const collapsedData = collapsed?.map((el, index) => ({ ...el, [savedName]: true }));
        setFormData((prev) => ({
            ...prev,
            caseStudy: collapsedData
        }))
        setCollapsedItems((prev) => {
            const updated = Object.fromEntries(
                Object.entries(prev)?.filter(([key, value]) => value.quoteBy !== savedName)
            );
            return updated;
        });
        form.setFieldsValue({
            caseStudy: collapsedData
        });
        setPictures((prev) => {
            const updated = { ...prev };
            delete updated[savedName];
            return updated;
        });
    };

    const handleFileChange = (savedName, fileList) => {
        setPictures(prevPictures => {
            const updatedPictures = { ...prevPictures };
            updatedPictures[savedName] = fileList.map(newFile => {
                const existingPicture = updatedPictures[savedName]?.find(pic => pic.uid === newFile.uid);
                return existingPicture ? { ...existingPicture, ...newFile } : newFile;
            });

            return updatedPictures;
        });
    };

    const processFileUpload = async ({ onSuccess, onError, file, savedName }) => {
        try {
            let presignedPostData = await getPresignedPostData(file);
            presignedPostData = await presignedPostData.data;
            await uploadFileToS3(presignedPostData, file);
            onSuccess(null, file);
            setPictures(prevPictures => {
                const updatedPictures = { ...prevPictures };
                if (!updatedPictures[savedName]) {
                    updatedPictures[savedName] = [];
                }
                updatedPictures[savedName] = updatedPictures[savedName].map(pic =>
                    pic.uid === file.uid
                        ? { ...pic, ...presignedPostData.fields }
                        : pic
                );
                if (!updatedPictures?.[savedName]?.find(pic => pic.uid === file.uid)) {
                    updatedPictures[savedName].push({ ...presignedPostData.fields, ...file });
                }

                return updatedPictures;
            });
        } catch (e) {
            antdMessage.error(`File upload failed. ${e.message || ''}`);
            onError(e);
        }
    };

    const handleDescriptionChange = (key) => (e) => {
        const newDescriptions = { ...mediaDescriptions, [key]: e.target.value };
        setMediaDescriptions(newDescriptions);
        const updatedPictures = mediaPictures.map((pic) =>
            pic.uid === key ? { ...pic, mediaDescription: e.target.value } : pic
        );
        setMediaPictures(updatedPictures);
    };

    const handleDocsDescriptionChange = (key) => (e) => {
        const newDescriptions = { ...docsMediaDescriptions, [key]: e.target.value };
        setDocsMediaDescriptions(newDescriptions);
        const updatedPictures = docs.map((pic) =>
            pic.uid === key ? { ...pic, mediaDescription: e.target.value } : pic
        );
        setDocs(updatedPictures);
    };

    const handlePictureDescriptionChange = (index, name, key) => (e) => {
        const newDescriptions = { ...pictureMediaDescriptions, [key]: e.target.value };
        setPictureMediaDescriptions(newDescriptions);
        const updatedPictures = [...pictures];
        const targetIndex = pictures[index] ? index : pictures[name] ? name : null;

        if (targetIndex !== null) {
            updatedPictures[targetIndex] = updatedPictures[targetIndex].map((pic) =>
                pic.uid === key ? { ...pic, mediaDescription: e.target.value } : pic
            );
        }
        setPictures(updatedPictures);
    };

    const getPresignedPostData = async (selectedFile) => new Promise((resolve, reject) => {
        const fileName = selectedFile?.name.replace(
            /(?:\.(?![^.]+$)|[^\w.])+/g,
            ''
        );
        const url = `${Config.dev.url.companyLogoUploadUrl}?fileName=${fileName}`;
        axios.get(url, (res, err) => {
            if (res && res.status === 200) {
                resolve(res.data);
            } else {
                reject(err.response);
            }
        });
    });

    const uploadFileToS3 = async (presignedPostData, file) =>
        new Promise((resolve, reject) => {
            const formData = new FormData();
            const config = {
                headers: { "content-type": "multipart/form-data" },
            };
            Object.keys(presignedPostData.fields).forEach((key) => {
                formData.append(key, presignedPostData.fields[key]);
            });
            formData.append("file", file);
            uploadFile(
                presignedPostData.url,
                formData,
                (res, err) => {
                    if (res && res.status === 204) {
                        resolve(res.data);
                    } else {
                        reject(err.response);
                    }
                },
                config
            );
        });

    const onPreview = async (file) => {
        let src = file.url;
        if (!src && file.key) {
            try {
                const blob = await getSecuredUrl(file.key, false);
                src = URL.createObjectURL(blob);
            } catch (error) {
                console.error('Failed to fetch secured URL:', error);
                return;
            }
        }
        if (!src && file?.originFileObj instanceof Blob) {
            src = await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file.originFileObj);
                reader.onload = () => resolve(reader.result);
                reader.onerror = () => reject(new Error('Failed to read file'));
            });
        }

        if (src) {
            const image = new Image();
            image.src = src;
            const imgWindow = window.open(src);
            imgWindow?.document.write(image.outerHTML);
        } else {
            console.error('Failed to preview image: Source is invalid or unavailable.');
        }
    };

    const onDelete = (file) => {
        setPictures((prevPictures = {}) => {
            const updatedPictures = Object.fromEntries(
                Object.entries(prevPictures).map(([key, files]) => [
                    key,
                    Array.isArray(files) ? files.filter(picture => picture.uid !== file.uid) : files
                ])
            );
            return updatedPictures;
        });
    };

    const onRemove = async (file) => {
        const { deleteURL } = Config.dev.url;
        const data = { bucketName: 'bg-uploaded-media', fileName: file.key };
        axios.put(deleteURL, () => onDelete(file), data, true);
    };

    const handleProceed = (e) => {
        const { caseStudy } = formData || {};
        const filteredCaseStudies = caseStudy?.reduce((acc, el, index) => {
            const existing = acc.find(item => item?.quoteBy === el?.quoteBy);
            if (!existing) {
                acc.push({
                    ...el,
                    documents: pictures?.[index]?.map(({ uid, name, url, type, key, mediaDescription }) => ({
                        uid, name, url, type, key, mediaDescription
                    })) || pictures[el.quoteBy]?.map(({ uid, name, url, type, key, mediaDescription }) => ({
                        uid, name, url, type, key, mediaDescription
                    })),
                });
            } else {
                const existingIndex = acc.findIndex(item => item?.quoteBy === el?.quoteBy);
                acc[existingIndex] = {
                    ...el,
                    documents: pictures?.[index]?.map(({ uid, name, url, type, key, mediaDescription }) => ({
                        uid, name, url, type, key, mediaDescription
                    })) || pictures[el.quoteBy]?.map(({ uid, name, url, type, key, mediaDescription }) => ({
                        uid, name, url, type, key, mediaDescription
                    })),
                };
            }

            return acc;
        }, []);

        const data = {
            contentId: ID,
            caseStudy: filteredCaseStudies,
            gallery: mediaPictures?.map(({ uid, name, url, mediaDescription, type, key }) => ({ uid, name, url, mediaDescription, type, key })),
            uploadedDocuments: docs?.map(({ uid, name, url, mediaDescription, key }) => ({ uid, name, url, mediaDescription, key })),
            action: "Draft",
            opportunityID: opportunityID,
            programID: programID
        }
        dispatch(editContent(data));
        dispatch(toggleSpinner(true));
    }

    const handleSaveAndExit = () => {
        setSavedClicked(true);
        setTimeout(() => {
            form.submit();
        }, 300);
    }

    const handlePrevious = () => {
        if (Array.isArray(indicators) && indicators?.length > 0) {
            navigate(`/impact-maker/content/final/indicators/${ID}`);
        } else {
            navigate(`/impact-maker/content/final/objectives/${ID}`);
        }
    }

    const onFormChange = (_, allValues) => {
        setFormData(allValues);
    };

    const processFileUploadMedia = async ({ onSuccess, onError, file }) => {
        try {
            let presignedPostData = await getPresignedPostData(file);
            presignedPostData = await presignedPostData.data;
            await uploadFileToS3(presignedPostData, file);
            onSuccess(null, file);
            setMediaPictures(prevPictures =>
                prevPictures.map(pic =>
                    pic.uid === file.uid ? { ...pic, ...presignedPostData.fields } : pic
                )
            );
        } catch (e) {
            antdMessage.error(`File upload failed. ${e.message || ''}`);
            onError(e);
        }
    };

    const processFileUploadDocs = async ({ onSuccess, onError, file }) => {
        try {
            let presignedPostData = await getPresignedPostData(file);
            presignedPostData = await presignedPostData.data;
            await uploadFileToS3(presignedPostData, file);
            onSuccess(null, file);
            setDocs(prevPictures =>
                prevPictures.map(pic =>
                    pic.uid === file.uid ? { ...pic, ...presignedPostData.fields } : pic
                )
            );
        } catch (e) {
            antdMessage.error(`File upload failed. ${e.message || ''}`);
            onError(e);
        }
    };

    const onChangeMedia = ({ fileList: newFileList }) => {
        setMediaPictures(prevPictures => {
            const updatedPictures = newFileList.map(newFile => {
                const existingPicture = prevPictures?.find(pic => pic.uid === newFile.uid);
                return existingPicture ? { ...existingPicture, ...newFile } : newFile;
            });
            return updatedPictures;
        });
    };

    const onChangeDocsMedia = ({ fileList: newFileList }) => {
        setDocs(prevPictures => {
            const updatedPictures = newFileList.map(newFile => {
                const existingPicture = prevPictures?.find(pic => pic.uid === newFile.uid);
                return existingPicture ? { ...existingPicture, ...newFile } : newFile;
            });
            return updatedPictures;
        });
    };

    const onDeleteMedia = (file) => {
        setMediaPictures((prevPictures) => {
            return prevPictures?.filter(picture => picture.uid !== file.uid)
        });
    }

    const onRemoveMedia = async (file) => {
        const { deleteURL } = Config.dev.url;
        const data = { bucketName: 'bg-uploaded-media', fileName: file.key };
        axios.put(deleteURL, () => onDeleteMedia(file), data, true);
    };

    const onDeleteDocsMedia = (file) => {
        setDocs((prevPictures) => {
            return prevPictures?.filter(picture => picture.uid !== file.uid)
        });
    }

    const onRemoveDocsMedia = async (file) => {
        const { deleteURL } = Config.dev.url;
        const data = { bucketName: 'bg-uploaded-media', fileName: file.key };
        axios.put(deleteURL, () => onDeleteDocsMedia(file), data, true);
    };

    const customItemRender = (originNode, file) => {
        if (file?.url && file?.type?.startsWith('image/')) {
            const containerStyle = {
                margin: '10px 0',
                position: 'relative',
            };

            const actionsStyle = {
                position: 'absolute',
                top: '50%',
                right: '25%',
                display: 'flex',
                gap: '5px',
            };

            const iconStyle = {
                color: '#fff',
                fontSize: '16px',
                cursor: 'pointer',
                background: 'rgba(0, 0, 0, 0.5)',
                padding: '5px',
                borderRadius: '4px',
            };
            return (
                <div style={containerStyle}>
                    <img
                        src={file.url}
                        alt={file.name}
                        style={{ maxWidth: '100%', maxHeight: '300px', objectFit: 'contain' }}
                    />
                    <div style={actionsStyle}>
                        <EyeOutlined
                            style={iconStyle}
                            onClick={() => onPreview(file)}
                        />
                        <DeleteOutlined
                            style={iconStyle}
                            onClick={() => onRemove(file)}
                        />
                    </div>
                </div>
            );
        }
        return originNode;
    };

    return (
        <div className={baseClassName}>
            {showSpinner && (
                <div className="overlay">
                    <Spin className="spinner" size="large" spinning={showSpinner} />
                </div>
            )}
            <Steps
                current={2}
                items={[
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_1" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "inputs_n_outputs" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_2" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "objectives_n_outcomes" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_3" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3c" className='mainTitle'>
                            {intl.formatMessage({ id: "case_studies" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_4" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "next_steps" })}
                        </StyledText>,
                    },
                    {
                        title: <StyledText as="p" variant="H3">
                            {intl.formatMessage({ id: "step_5" })}
                        </StyledText>,
                        description: <StyledText as="p" variant="B3" className='mainTitle'>
                            {intl.formatMessage({ id: "preview_n_submit" })}
                        </StyledText>,
                    },
                ]}
            />
            <div className='contentContainer'>
                <div>
                    <StyledText as="p" variant="H1" className='mainTitle'>
                        {intl.formatMessage({ id: "case_studies_and_media" })}
                    </StyledText>

                    <Form
                        {...formItemLayout}
                        labelAlign="left"
                        onFinish={handleProceed}
                        onFinishFailed={onFinishFailed}
                        form={form}
                        className="formContainer"
                        scrollToFirstError
                        onValuesChange={onFormChange}
                    >
                        <div className="mainContentContainer">
                            <div className="contentContainer">
                                <StyledText as="p" variant="B3">
                                    {intl.formatMessage({ id: "case_studies_and_images_increase_the_likelihood_of_the_company_sharing_your_project_where_possible_please_share_a_case_study_and_at_least_one_image" })}
                                </StyledText>
                                <StyledText as="span" variant="B3d" className='mainTitle'>
                                    {intl.formatMessage({ id: "case_studies" })}
                                    <StyledText as="span" variant="B3a">
                                        {` (${intl.formatMessage({ id: "optional" })})`}
                                    </StyledText>
                                </StyledText>
                                {<>
                                    <Form.List name="caseStudy">
                                        {(fields, { add, remove }) => (
                                            <>
                                                <Button variant="primary" onClick={() => add()} className='addCaseStudy'>
                                                    {intl.formatMessage({ id: "add_case_study" })}
                                                </Button>
                                                {fields.map(({ key, name, ...restField }, index) => {
                                                    const savedName = formData?.caseStudy?.[index]?.quoteBy;
                                                    return (
                                                        <Space
                                                            key={key}
                                                            style={{
                                                                marginBottom: 0,
                                                                display: 'flex',
                                                                flexDirection: 'column',
                                                                alignItems: 'flex-start'
                                                            }}
                                                            className='optionsContainer'
                                                        >
                                                            {Object.values(collapsedItems)?.find(el => el?.[savedName]) ? (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                                    <StyledText
                                                                        as="span"
                                                                        variant="B3"
                                                                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                                                                        onClick={() => handleEdit(index, savedName)}
                                                                    >
                                                                        {formData.caseStudy?.[index]?.name || '-'}
                                                                    </StyledText>
                                                                    <MinusCircleOutlined
                                                                        onClick={() => handleRemove(savedName, remove)}
                                                                        style={{ cursor: 'pointer', color: 'red' }}
                                                                    />
                                                                </div>
                                                            ) : (
                                                                <>
                                                                    <StyledText as="span" variant="B3" className='mainTitle'>
                                                                        {intl.formatMessage({ id: "case_study_name" })}
                                                                    </StyledText>
                                                                    <Form.Item
                                                                        {...restField}
                                                                        name={[name, 'name']}
                                                                        className="formItemTextFieldPart"
                                                                        rules={[
                                                                            { required: true, message: intl.formatMessage({ id: "enter" }) }
                                                                        ]}
                                                                    >
                                                                        <Input />
                                                                    </Form.Item>

                                                                    <StyledText as="span" variant="B3" className='mainTitle'>
                                                                        {intl.formatMessage({ id: "what_impact_did_your_project_have" })}
                                                                    </StyledText>
                                                                    <Form.Item
                                                                        {...restField}
                                                                        name={[name, 'outcome']}
                                                                        className="formItemTextFieldPart"
                                                                        rules={[
                                                                            { required: true, message: intl.formatMessage({ id: "enter" }) }
                                                                        ]}
                                                                    >
                                                                        <TextArea maxLength={300} rows={2} />
                                                                    </Form.Item>
                                                                    <StyledText as="span" variant="B3" className='mainTitle'>
                                                                        {intl.formatMessage({ id: "share_a_quote_such_as_from_a_beneficiary" })}
                                                                        <StyledText as="span" variant="B3a">
                                                                            {` (${intl.formatMessage({ id: "optional" })})`}
                                                                        </StyledText>
                                                                    </StyledText>
                                                                    <Form.Item
                                                                        {...restField}
                                                                        name={[name, 'shareQuote']}
                                                                        className="formItemTextFieldPart"
                                                                    >
                                                                        <TextArea maxLength={300} rows={2} />
                                                                    </Form.Item>

                                                                    <StyledText as="span" variant="B3" className='mainTitle'>
                                                                        {intl.formatMessage({ id: "quote_by" })}
                                                                    </StyledText>
                                                                    <Form.Item
                                                                        {...restField}
                                                                        rules={[
                                                                            { required: true, message: intl.formatMessage({ id: "enter" }) }
                                                                        ]}
                                                                        name={[name, 'quoteBy']}
                                                                        className="formItemTextFieldPart"
                                                                    >
                                                                        <Input />
                                                                    </Form.Item>
                                                                    <StyledText as="span" variant="B3" className='mainTitle'>
                                                                        {intl.formatMessage({ id: "share_a_photo_or_other_media" })}
                                                                        <StyledText as="span" variant="B3a">
                                                                            {` (${intl.formatMessage({ id: "optional" })})`}
                                                                        </StyledText>
                                                                    </StyledText>
                                                                    <Form.Item className='uploadWrapper'>
                                                                        <Upload
                                                                            listType="picture-card"
                                                                            onChange={({ fileList }) => handleFileChange(savedName, fileList)}
                                                                            fileList={pictures?.[index]?.length > 0 ? pictures?.[index] : pictures?.[savedName]?.length > 0 ? pictures?.[savedName] : []}
                                                                            onPreview={onPreview}
                                                                            onRemove={onRemove}
                                                                            customRequest={({ onSuccess, onError, file }) =>
                                                                                processFileUpload({ onSuccess, onError, file, savedName })
                                                                            }
                                                                            customItemRender={customItemRender}
                                                                        >
                                                                            {`+ ${intl.formatMessage({ id: "upload" })}`}
                                                                        </Upload>
                                                                        <div className='uploadInputWrapperInner'>
                                                                            {(pictures?.[index] || pictures?.[savedName])?.map((file) => (
                                                                                <Form.Item key={file.uid}>
                                                                                    <Input
                                                                                        placeholder={intl.formatMessage({ id: "enter_caption" })}
                                                                                        value={pictureMediaDescriptions[file.uid] || ''}
                                                                                        onChange={handlePictureDescriptionChange(index, savedName, file.uid)}
                                                                                    />
                                                                                </Form.Item>
                                                                            ))}
                                                                        </div>
                                                                    </Form.Item>

                                                                    <div style={{ display: 'flex', gap: '20px' }}>
                                                                        <Button variant="secondary" onClick={() => {
                                                                            setCollapsedItems((prev) => {
                                                                                if (!prev) return {};
                                                                                const updatedItems = { ...prev };
                                                                                Object.keys(updatedItems).forEach((key) => {
                                                                                    if (
                                                                                        updatedItems?.[index] &&
                                                                                        typeof updatedItems[index] === 'object' &&
                                                                                        updatedItems[index]?.[savedName] !== undefined
                                                                                    ) {
                                                                                        updatedItems[index] = {
                                                                                            ...updatedItems[index],
                                                                                            [savedName]: true
                                                                                        };
                                                                                    }
                                                                                });
                                                                                return updatedItems;
                                                                            });
                                                                        }}>
                                                                            {intl.formatMessage({ id: "cancel" })}
                                                                        </Button>
                                                                        <Button variant="primary" onClick={() => handleSave(index, savedName)}>
                                                                            {intl.formatMessage({ id: "save" })}
                                                                        </Button>
                                                                    </div>
                                                                </>
                                                            )}
                                                        </Space>
                                                    );
                                                })}

                                            </>
                                        )}
                                    </Form.List>
                                </>}
                                <StyledText as="span" variant="B3d" className='uploadWrapperTitle'>
                                    {intl.formatMessage({ id: "add_images" })}
                                    <StyledText as="span" variant="B3a">
                                        {` (${intl.formatMessage({ id: "optional" })})`}
                                    </StyledText>
                                </StyledText>
                                <StyledText as="span" variant="B3">
                                    {intl.formatMessage({ id: "upload_any_image_files_here_png_jpg" })}
                                </StyledText>
                                <Form.Item className='uploadWrapper'>
                                    <Upload
                                        listType="picture-card"
                                        onChange={({ fileList, file, ...rest }) => {
                                            const validFiles = fileList.filter(item =>
                                                ['image/png', 'image/jpg', 'image/jpeg', 'image/tiff'].includes(item.type)
                                            );

                                            if (file.status === 'error' && !validFiles.includes(file)) {
                                                antdMessage.error(intl.formatMessage({ id: "please_upload_this_file_type_in_the_add_files_section_below" }));
                                            }

                                            onChangeMedia({
                                                fileList: validFiles,
                                                ...rest
                                            });
                                        }}
                                        fileList={mediaPictures}
                                        onPreview={onPreview}
                                        onRemove={onRemoveMedia}
                                        beforeUpload={(file) => {
                                            const isValidType = ['image/png', 'image/jpg', 'image/jpeg', 'image/tiff'].includes(file.type);
                                            if (!isValidType) {
                                                antdMessage.error(intl.formatMessage({ id: "please_upload_this_file_type_in_the_add_files_section_below" }));
                                            }
                                            return isValidType;
                                        }}
                                        customRequest={({ onSuccess, onError, file }) => {
                                            if (!['image/png', 'image/jpg', 'image/jpeg', 'image/tiff'].includes(file.type)) {
                                                antdMessage.error(intl.formatMessage({ id: "please_upload_this_file_type_in_the_add_files_section_below" }));
                                                onError(new Error('Invalid file type.'));
                                                return;
                                            }
                                            processFileUploadMedia({
                                                onSuccess, onError, file
                                            });
                                        }}
                                        itemRender={customItemRender}
                                        multiple
                                    >
                                        {`+ ${intl.formatMessage({ id: "upload" })}`}
                                    </Upload>
                                    <div className='uploadInputWrapper'>
                                        {mediaPictures.map((file) => (
                                            <Form.Item key={file.uid}>
                                                <Input
                                                    placeholder={intl.formatMessage({ id: "enter_caption" })}
                                                    value={mediaDescriptions[file.uid] || ''}
                                                    onChange={handleDescriptionChange(file.uid)}
                                                />
                                            </Form.Item>
                                        ))}
                                    </div>
                                </Form.Item>
                                <StyledText as="span" variant="B3d" className='uploadWrapperTitle'>
                                    {intl.formatMessage({ id: "add_files" })}
                                    <StyledText as="span" variant="B3a">
                                        {` (${intl.formatMessage({ id: "optional" })})`}
                                    </StyledText>
                                </StyledText>
                                <StyledText as="span" variant="B3">
                                    {intl.formatMessage({ id: "upload_any_documents_that_you_wish_to_share" })}
                                </StyledText>
                                <Form.Item className='uploadWrapper'>
                                    <Upload
                                        listType="picture-card"
                                        onChange={({ fileList, file, ...rest }) => {
                                            const validFiles = fileList.filter(item =>
                                                ['application/msword', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(item.type)
                                            );

                                            if (file.status === 'error' && !validFiles.includes(file)) {
                                                antdMessage.error(intl.formatMessage({ id: "please_upload_images_in_the_additional_images_section_above" }));
                                            }

                                            onChangeDocsMedia({
                                                fileList: validFiles,
                                                ...rest
                                            });
                                        }}
                                        fileList={docs}
                                        onPreview={onPreview}
                                        onRemove={onRemoveDocsMedia}
                                        beforeUpload={(file) => {
                                            const isValidType = ['application/msword', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(file.type)
                                            if (!isValidType) {
                                                antdMessage.error(intl.formatMessage({ id: "please_upload_images_in_the_additional_images_section_above" }));
                                            }
                                            return isValidType;
                                        }}
                                        customRequest={({ onSuccess, onError, file }) => {
                                            if (!['application/msword', 'application/pdf', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'].includes(file.type)) {
                                                antdMessage.error(intl.formatMessage({ id: "please_upload_images_in_the_additional_images_section_above" }));
                                                onError(new Error('Invalid file type.'));
                                                return;
                                            }
                                            processFileUploadDocs({
                                                onSuccess, onError, file
                                            });
                                        }}
                                        multiple
                                    >
                                        {`+ ${intl.formatMessage({ id: "upload" })}`}
                                    </Upload>
                                    <div className='uploadInputWrapper'>
                                        {docs.map((file) => (
                                            <Form.Item key={`${file.uid}-uploadedMedia`}>
                                                <Input
                                                    placeholder={intl.formatMessage({ id: "enter_caption" })}
                                                    value={docsMediaDescriptions[file.uid] || ''}
                                                    onChange={handleDocsDescriptionChange(file.uid)}
                                                />
                                            </Form.Item>
                                        ))}
                                    </div>
                                </Form.Item>
                            </div>
                            <div className="buttonContainer">
                                <Divider />
                                <div className="buttonContentContainer">
                                    <div className="nextAndPreviousContainer">
                                        <Button variant="secondary" type="button" onClick={() => handlePrevious()}>
                                            {intl.formatMessage({ id: "previous" })}
                                        </Button>
                                        <Button variant="primary" htmlType="submit">
                                            {intl.formatMessage({ id: "next" })}
                                        </Button>
                                    </div>
                                    <div className="saveAndExitButton">
                                        <Button variant="secondary" htmlType="button" onClick={() => handleSaveAndExit()}>
                                            {intl.formatMessage({ id: "save_n_exit" })}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Form>
                </div>
            </div>
        </div>
    )
}

const StyledCharityFinalCaseStudies = styled(CharityFinalCaseStudies)`
  &.charityFinalCaseStudies {
    background: white;
    display: flex;
    flex-direction: column;
    padding: 20px;
    justify-content: space-between;
    margin-top: 50px;
    .addCaseStudy{
        width: max-content;
    }
    .uploadWrapper .ant-form-item-control-input-content{
        display: flex !important;
        width: 100%;
        .ant-upload-list{
        display: flex;
        flex-direction: column !important;
    }
    .uploadInputWrapper{
        gap: 56px;
        display: flex;
        flex-direction: column;
        margin-top: 30px;
        margin-left: 30px;
        width: 100%;
    }
    }
    .ant-upload-select{
        min-width: 112px !important;
    }

    .uploadWrapperTitle{
       padding-top: 50px;
    }

    .uploadInputWrapperInner{
        width: 50vw;
        gap: 56px;
        display: flex;
        flex-direction: column;
        margin-top: 30px;
        margin-left: 30px;
    }

    .whiteSupportContainer{
        background: white;
        box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
          padding: 16px;
          width: 35vw;
          &.volunteeringContainer {
         box-shadow: 0px 3px 16px #0000000d;
          border-radius: 10px;
          display: flex;
          flex-direction: column;
          gap: 16px;
        }
    }

    .mainTitle {
      margin-bottom: 20px;
    }

    .formItem .ant-form-item-explain-error {
      margin-top: -20px !important;
    }

    .contentContainer {
      display: flex;
      flex-direction: column;
      gap: 12px;
      min-height: 60vh;
      padding-bottom: 60px;

      .formContainer {
        display: flex;
        gap: 12px;
        flex-direction: column;
      }
    }

    .overlay {
      position: fixed;
      top: 0;
      right: 0;
      bottom: 0;
      left: 0;
      z-index: 1000;
      height: 100%;
      background-color: rgba(255, 255, 255, 0.45);
    }

    .spinner {
      z-index: 2000;
      margin: auto;
      left: 50%;
      right: 50%;
      position: absolute;
      bottom: 50%;
    }

    .radioGroup {
      display: flex;
      flex-direction: column;
    }

    .formItemTextField {
      .ant-form-item-control-input {
        width: 34.5vw !important;
      }
    }

    .formItemTextFieldPart {
      .ant-form-item-control-input {
        width: 16vw !important;
      }
    }

    .helperText {
      color: #5271FF !important;
      font-weight: bold !important;
      margin-bottom: 0px !important;
    }

    .ant-form-item {
      margin-bottom: 10px;
    }

    .formItemInput {
      width: 300px;
    }

    .mainTitle{
        margin-bottom: 20px;
    }
    .errorText{
        color: #ff4d4f;
    }
    .contentContainer{
        display: flex;
    }
    .addButton{
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
    }
    .formContainer{
        display: flex;
        gap: 12px;
        flex-direction: column;
    }
    .optionsContainer{
        display: flex;
        align-items: center;
        gap: 5px;
        .for{
            margin-bottom: 10px;
        }
    }

    .radioGroup {
            display: flex;
            flex-direction: column;
    }

    .buttonContainer{
        display: flex;
        justify-content: center;
        flex-direction: column;
        align-items: center;
        position: fixed;
        width: 100%;
        bottom: 0px;
        background: white;
        margin-left: -110px;
        .buttonContentContainer{
            display: flex;
            justify-content: center;
            width: 100%;
            position: relative;
            .saveAndExitButton{
                position: absolute;
                right: 0;
                margin-bottom: 15px;
            }
            .nextAndPreviousContainer{
                display: flex;
                gap: 20px;
                margin-bottom: 15px;
            }
        }
    }
    .formItemTextField{
        .ant-form-item-control-input{
            width: 34.5vw !important;
        }
    }
    .formItemTextFieldPart{
        .ant-form-item-control-input{
            width: 16vw !important;
        } 
    }
    .helperText {
    color: #5271FF !important;
    font-weight: bold !important;
    margin-bottom: 0px !important;
  }
  .ant-form-item{
    margin-bottom: 10px;
  }
  .formItemInput{
    width: 300px;
  }
  }
`;

export default StyledCharityFinalCaseStudies;
